import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { Add, Delete } from "@mui/icons-material";
import { useState, useEffect } from "react";
import {
  BillingMethod,
  BillingRuleCondition,
  BillingRuleConditionOperator,
  BillingRuleConditionParameter,
  BillingRuleConditionTarget,
  BillingRuleType,
  GetCustomerDetailsQuery,
  RateTable,
  RateTableType,
} from "../../../graphql/generated";
import RateForm from "../BillingRuleForm/RateForm";
import EffectiveDatesFields from "../EffectiveDates/EffectiveDatesFields";
import ConditionValueSelector from "./ConditionValueSelector";
import EnumSelect from "../../common/EnumSelect/EnumSelect";
import billingRuleConditionTargetLabel from "../../../utils/labels/billingRule/billingRuleConditionTargetLabel";
import billingRuleConditionOperatorLabel from "../../../utils/labels/billingRule/billingRuleConditionOperatorLabel";
import { useTranslation } from "react-i18next";
import { testIds } from "./RatingTableEditor.testIds";
import {
  buildConditionParameters,
  visibleConditionParameters,
} from "../conditionParameters";
import ConditionParameterForm from "../BillingRuleForm/ConditionParameterForm";

export type RatingTableItem = Omit<
  RateTable,
  "createdAt" | "updatedAt" | "entityType" | "entityId" | "customer" | "driver"
>;

type RatingTableEditorProps = {
  ratingTable: RatingTableItem;
  customer?: GetCustomerDetailsQuery["customerById"];
  onChange: (ratingTable: RatingTableItem) => void;
  onSave: () => void;
  onCancel: () => void;
  saving: boolean;
  showTaxable?: boolean;
};

// Only "is between" stores a [min, max] pair. Other operators keep the value as
// it is, so targets that hold several ids do not lose them.
const convertValueForOperator = (
  value: BillingRuleCondition["value"],
  previousOperator: BillingRuleConditionOperator,
  newOperator: BillingRuleConditionOperator
) => {
  if (newOperator === BillingRuleConditionOperator.IsBetween) {
    return Array.isArray(value) ? value : [value, null];
  }
  if (previousOperator === BillingRuleConditionOperator.IsBetween) {
    return Array.isArray(value) ? value[0] : value;
  }
  return value;
};

const RatingTableEditor = ({
  ratingTable,
  customer,
  onChange,
  onSave,
  onCancel,
  saving,
  showTaxable = true,
}: RatingTableEditorProps) => {
  const { t } = useTranslation("billingRules");

  const [showColumnAxis, setShowColumnAxis] = useState(
    !!ratingTable.columnAxis
  );

  // Axis configurations - target and operator for each axis
  const [rowAxisConfig, setRowAxisConfig] = useState({
    target:
      ratingTable.rowAxis.conditions[0]?.target ||
      BillingRuleConditionTarget.TrailerType,
    operator:
      ratingTable.rowAxis.conditions[0]?.operator ||
      BillingRuleConditionOperator.Equals,
  });

  // The parameters belong to the axis, not to a single row, so they are read
  // from the first condition and written back to all of them.
  const rowAxisParameters = buildConditionParameters(
    rowAxisConfig.target,
    ratingTable.rowAxis.conditions[0]?.parameters
  );

  const [columnAxisConfig, setColumnAxisConfig] = useState({
    target:
      ratingTable.columnAxis?.conditions[0]?.target ||
      BillingRuleConditionTarget.Receiver,
    operator:
      ratingTable.columnAxis?.conditions[0]?.operator ||
      BillingRuleConditionOperator.Equals,
  });

  const columnAxisParameters = buildConditionParameters(
    columnAxisConfig.target,
    ratingTable.columnAxis?.conditions[0]?.parameters
  );

  // Sync showColumnAxis with the actual columnAxis presence
  useEffect(() => {
    setShowColumnAxis(!!ratingTable.columnAxis);
  }, [ratingTable.columnAxis]);

  // Helper function to update all row conditions when row axis config changes
  const updateRowAxisConditions = (
    newTarget: BillingRuleConditionTarget,
    newOperator: BillingRuleConditionOperator
  ) => {
    setRowAxisConfig({ target: newTarget, operator: newOperator });
    const parameters = buildConditionParameters(
      newTarget,
      ratingTable.rowAxis.conditions[0]?.parameters
    );
    onChange({
      ...ratingTable,
      rowAxis: {
        conditions: ratingTable.rowAxis.conditions.map((condition) => ({
          ...condition,
          target: newTarget,
          operator: newOperator,
          parameters,
          value: convertValueForOperator(
            condition.value,
            rowAxisConfig.operator,
            newOperator
          ),
        })),
      },
    });
  };

  // Helper function to update all column conditions when column axis config changes
  const updateColumnAxisConditions = (
    newTarget: BillingRuleConditionTarget,
    newOperator: BillingRuleConditionOperator
  ) => {
    setColumnAxisConfig({ target: newTarget, operator: newOperator });
    if (ratingTable.columnAxis) {
      const parameters = buildConditionParameters(
        newTarget,
        ratingTable.columnAxis.conditions[0]?.parameters
      );
      onChange({
        ...ratingTable,
        columnAxis: {
          conditions: ratingTable.columnAxis.conditions.map((condition) => ({
            ...condition,
            target: newTarget,
            operator: newOperator,
            parameters,
            value: convertValueForOperator(
              condition.value,
              columnAxisConfig.operator,
              newOperator
            ),
          })),
        },
      });
    }
  };

  // The axis owns the parameters, so a change is written to every condition on
  // that axis.
  const updateAxisParameter = (
    axis: "row" | "column",
    parameter: BillingRuleConditionParameter
  ) => {
    const current = axis === "row" ? rowAxisParameters : columnAxisParameters;
    const parameters = current.map((existing) =>
      existing.type === parameter.type ? parameter : existing
    );
    const applyTo = (conditions: BillingRuleCondition[]) =>
      conditions.map((condition) => ({ ...condition, parameters }));

    if (axis === "row") {
      onChange({
        ...ratingTable,
        rowAxis: { conditions: applyTo(ratingTable.rowAxis.conditions) },
      });
      return;
    }
    if (ratingTable.columnAxis) {
      onChange({
        ...ratingTable,
        columnAxis: { conditions: applyTo(ratingTable.columnAxis.conditions) },
      });
    }
  };

  const renderAxisParameters = (axis: "row" | "column") => {
    const parameters = axis === "row" ? rowAxisParameters : columnAxisParameters;
    return visibleConditionParameters(parameters).map((parameter) => (
      <ConditionParameterForm
        key={parameter.type}
        conditionParameter={parameter}
        onChange={(updated) => updateAxisParameter(axis, updated)}
      />
    ));
  };

  const handleAddRowCondition = () => {
    const columnCount = ratingTable.columnAxis?.conditions.length || 1;

    onChange({
      ...ratingTable,
      rowAxis: {
        conditions: [
          ...ratingTable.rowAxis.conditions,
          {
            target: rowAxisConfig.target,
            operator: rowAxisConfig.operator,
            value: null,
            parameters: rowAxisParameters,
          },
        ],
      },
      cells: [
        ...ratingTable.cells,
        new Array(columnCount).fill(null).map(() => ({
          rates: [
            {
              method: BillingMethod.FlatFee,
              amount: 0,
              label: "",
              referenceNumber: "",
              parameters: [],
            },
          ],
        })),
      ],
    });
  };

  const handleAddColumnCondition = () => {
    if (!ratingTable.columnAxis) {
      setShowColumnAxis(true);
      onChange({
        ...ratingTable,
        columnAxis: {
          conditions: [
            {
              target: columnAxisConfig.target,
              operator: columnAxisConfig.operator,
              value: null,
              parameters: columnAxisParameters,
            },
          ],
        },
        cells: ratingTable.cells.map((row) => [
          ...row,
          {
            rates: [
              {
                method: BillingMethod.FlatFee,
                amount: 0,
                label: "",
                referenceNumber: "",
                parameters: [],
              },
            ],
          },
        ]),
      });
    } else {
      onChange({
        ...ratingTable,
        columnAxis: {
          conditions: [
            ...ratingTable.columnAxis.conditions,
            {
              target: columnAxisConfig.target,
              operator: columnAxisConfig.operator,
              value: null,
              parameters: columnAxisParameters,
            },
          ],
        },
        cells: ratingTable.cells.map((row) => [
          ...row,
          {
            rates: [
              {
                method: BillingMethod.FlatFee,
                amount: 0,
                label: "",
                referenceNumber: "",
                parameters: [],
              },
            ],
          },
        ]),
      });
    }
  };

  const handleInitializeColumnAxis = () => {
    setShowColumnAxis(true);
    onChange({
      ...ratingTable,
      columnAxis: {
        conditions: [
          {
            target: columnAxisConfig.target,
            operator: columnAxisConfig.operator,
            value: null,
            parameters: columnAxisParameters,
          },
        ],
      },
      cells: ratingTable.cells.map((row) => [
        ...row,
        {
          rates: [
            {
              method: BillingMethod.FlatFee,
              amount: 0,
              label: "",
              referenceNumber: "",
              parameters: [],
            },
          ],
        },
      ]),
    });
  };

  const handleRemoveRowCondition = (index: number) => {
    if (ratingTable.rowAxis.conditions.length <= 1) {
      return; // Prevent removing the last row
    }

    const newCells = [...ratingTable.cells];
    newCells.splice(index, 1);

    onChange({
      ...ratingTable,
      rowAxis: {
        conditions: ratingTable.rowAxis.conditions.filter(
          (_, i) => i !== index
        ),
      },
      cells: newCells,
    });
  };

  const handleRemoveColumnCondition = (index: number) => {
    if (ratingTable.columnAxis) {
      const newColumnConditions = ratingTable.columnAxis.conditions.filter(
        (_, i) => i !== index
      );

      if (newColumnConditions.length === 0) {
        setShowColumnAxis(false);
        onChange({
          ...ratingTable,
          columnAxis: null,
          cells: ratingTable.cells.map((row) => [row[0]]),
        });
      } else {
        onChange({
          ...ratingTable,
          columnAxis: {
            conditions: newColumnConditions,
          },
          cells: ratingTable.cells.map((row) =>
            row.filter((_, j) => j !== index)
          ),
        });
      }
    }
  };

  const isValidTable = () => {
    return (
      ratingTable.label &&
      ratingTable.label.trim().length > 0 &&
      ratingTable.rateType &&
      ratingTable.rowAxis.conditions.length > 0 &&
      ratingTable.cells.length > 0
    );
  };

  const is2D = showColumnAxis && ratingTable.columnAxis;

  return (
    <Box>
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {t("ratingTableDetails", "Rating Table Details")}
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t("form.label", "Label")}
                value={ratingTable.label || ""}
                onChange={(e) =>
                  onChange({
                    ...ratingTable,
                    label: e.target.value,
                  })
                }
                size="small"
                required
              inputProps={{ "data-testid": testIds.ratingTableEditorLabel } as React.InputHTMLAttributes<HTMLInputElement>}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <EnumSelect data-testid="rating-table-editor-rate-table-type"
                enumObject={RateTableType}
                label={t("billingType", "Billing Type")}
                value={ratingTable.rateType}
                onChange={(_, value) =>
                  onChange({
                    ...ratingTable,
                    rateType: value || RateTableType.Primary,
                  })
                }
                size="small"
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label={t("form.description", "Description")}
                value={ratingTable.description || ""}
                onChange={(e) =>
                  onChange({
                    ...ratingTable,
                    description: e.target.value,
                  })
                }
                size="small"
              inputProps={{ "data-testid": testIds.ratingTableEditorDescription } as React.InputHTMLAttributes<HTMLInputElement>}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t("form.reference", "Reference Number")}
                value={ratingTable.referenceNumber || ""}
                onChange={(e) =>
                  onChange({
                    ...ratingTable,
                    referenceNumber: e.target.value,
                  })
                }
                size="small"
              inputProps={{ "data-testid": testIds.ratingTableEditorReferenceNumber } as React.InputHTMLAttributes<HTMLInputElement>}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                type="number"
                label={t("form.priority", "Priority")}
                value={ratingTable.priority || ""}
                onChange={(e) =>
                  onChange({
                    ...ratingTable,
                    priority: parseFloat(e.target.value) || 0,
                  })
                }
                size="small"
              inputProps={{ "data-testid": testIds.ratingTableEditorPriority } as React.InputHTMLAttributes<HTMLInputElement>}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={ratingTable.isActive}
                    onChange={(e) =>
                      onChange({
                        ...ratingTable,
                        isActive: e.target.checked,
                      })
                    }
                  inputProps={{ "data-testid": testIds.ratingTableEditorActive } as React.InputHTMLAttributes<HTMLInputElement>}
                  />
                }
                label={t("active", "Active")}
              />
            </Grid>
            <Grid item xs={12}>
              <EffectiveDatesFields
                effectiveStartDate={ratingTable.effectiveStartDate}
                effectiveEndDate={ratingTable.effectiveEndDate}
                onChange={(patch) => onChange({ ...ratingTable, ...patch })}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Typography variant="h6" gutterBottom>
        {t("ratingTableMatrix", "Rating Table Matrix")}
      </Typography>

      {/* Axis Configuration Section */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="subtitle1" gutterBottom>
            {t("axisConfiguration", "Axis Configuration")}
          </Typography>
          <Grid container spacing={3}>
            {/* Row Axis Configuration */}
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" gutterBottom>
                {t("rowAxis", "Row Axis")}
              </Typography>
              <Stack spacing={2}>
                <Select data-testid={testIds.ratingTableEditorRowAxisTarget}
                  label="Condition Target"
                  size="small"
                  value={rowAxisConfig.target}
                  onChange={(event) =>
                    updateRowAxisConditions(
                      event.target.value as BillingRuleConditionTarget,
                      rowAxisConfig.operator
                    )
                  }
                  fullWidth
                >
                  {Object.values(BillingRuleConditionTarget).map((target) => (
                    <MenuItem data-testid={`${testIds.ratingTableEditorRowAxisTargetOption}-${target}`} key={target} value={target}>
                      <ListItemText
                        primary={billingRuleConditionTargetLabel(target)}
                      />
                    </MenuItem>
                  ))}
                </Select>
                <Select data-testid={testIds.ratingTableEditorRowAxisOperator}
                  label="Operator"
                  size="small"
                  value={rowAxisConfig.operator}
                  onChange={(event) =>
                    updateRowAxisConditions(
                      rowAxisConfig.target,
                      event.target.value as BillingRuleConditionOperator
                    )
                  }
                  fullWidth
                >
                  {Object.values(BillingRuleConditionOperator).map(
                    (operator) => (
                      <MenuItem data-testid={`${testIds.ratingTableEditorRowAxisOperatorOption}-${operator}`} key={operator} value={operator}>
                        <ListItemText
                          primary={billingRuleConditionOperatorLabel(operator)}
                        />
                      </MenuItem>
                    )
                  )}
                </Select>
                {renderAxisParameters("row")}
              </Stack>
            </Grid>

            {/* Column Axis Configuration */}
            {showColumnAxis && (
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" gutterBottom>
                  {t("columnAxis", "Column Axis")}
                </Typography>
                <Stack spacing={2}>
                  <Select data-testid={testIds.ratingTableEditorColumnAxisTarget}
                    label="Condition Target"
                    size="small"
                    value={columnAxisConfig.target}
                    onChange={(event) =>
                      updateColumnAxisConditions(
                        event.target.value as BillingRuleConditionTarget,
                        columnAxisConfig.operator
                      )
                    }
                    fullWidth
                  >
                    {Object.values(BillingRuleConditionTarget).map((target) => (
                      <MenuItem data-testid={`${testIds.ratingTableEditorColumnAxisTargetOption}-${target}`} key={target} value={target}>
                        <ListItemText
                          primary={billingRuleConditionTargetLabel(target)}
                        />
                      </MenuItem>
                    ))}
                  </Select>
                  <Select data-testid={testIds.ratingTableEditorColumnAxisOperator}
                    label="Operator"
                    size="small"
                    value={columnAxisConfig.operator}
                    onChange={(event) =>
                      updateColumnAxisConditions(
                        columnAxisConfig.target,
                        event.target.value as BillingRuleConditionOperator
                      )
                    }
                    fullWidth
                  >
                    {Object.values(BillingRuleConditionOperator).map(
                      (operator) => (
                        <MenuItem data-testid={`${testIds.ratingTableEditorColumnAxisOperatorOption}-${operator}`} key={operator} value={operator}>
                          <ListItemText
                            primary={billingRuleConditionOperatorLabel(
                              operator
                            )}
                          />
                        </MenuItem>
                      )
                    )}
                  </Select>
                  {renderAxisParameters("column")}
                </Stack>
              </Grid>
            )}
          </Grid>
        </CardContent>
      </Card>

      {!showColumnAxis && (
        <Box sx={{ mb: 2 }}>
          <Button data-testid={testIds.ratingTableEditorAddColumnAxis} variant="outlined" onClick={handleInitializeColumnAxis}>
            {t("addColumnAxis", "Add Column Axis (Make 2D)")}
          </Button>
        </Box>
      )}

      <TableContainer component={Paper}>
        <Table
          size="small"
          sx={{
            "& .MuiTableCell-root": {
              borderStyle: "solid",
              borderWidth: 1,
              borderColor: (theme) => theme.palette.primary.main,
            },
            borderCollapse: "separate",
          }}
        >
          <TableHead>
            <TableRow>
              <TableCell
                sx={(theme) => ({
                  minWidth: 350,
                  backgroundColor: `${theme.palette.primaryBackground.main} !important`,
                })}
              >
                <Typography
                  variant="subtitle2"
                  fontWeight="bold"
                  sx={{
                    color: (theme) => theme.palette.primary.main,
                  }}
                >
                  {t("rowConditions", "Row Conditions")}
                  {is2D && " / " + t("columnConditions", "Column Conditions")}
                </Typography>
              </TableCell>
              {is2D &&
                ratingTable.columnAxis?.conditions.map((condition, index) => (
                  <TableCell
                    key={index}
                    sx={(theme) => ({
                      minWidth: 300,
                      backgroundColor: `${theme.palette.primaryBackground.main} !important`,
                    })}
                  >
                    <Stack spacing={1} direction="row">
                      <ConditionValueSelector
                        target={columnAxisConfig.target}
                        operator={columnAxisConfig.operator}
                        value={condition.value}
                        customer={customer}
                        placeholder={`Column ${index + 1} value`}
                        onChange={(value) => {
                          onChange({
                            ...ratingTable,
                            columnAxis: {
                              conditions:
                                ratingTable.columnAxis!.conditions.map((c, i) =>
                                  i === index ? { ...c, value } : c
                                ),
                            },
                          });
                        }}
                      />
                      <Box display="flex" justifyContent="center">
                        <IconButton data-testid={`${
                            testIds.ratingTableEditorRemoveColumnCondition
                          }-${String(condition.value)}`}
                          size="small"
                          onClick={() => handleRemoveColumnCondition(index)}
                          color="error"
                        >
                          <Delete />
                        </IconButton>
                      </Box>
                    </Stack>
                  </TableCell>
                ))}
              {is2D && (
                <TableCell
                  sx={(theme) => ({
                    width: 60,
                    backgroundColor: `${theme.palette.primaryBackground.main} !important`,
                  })}
                >
                  <IconButton data-testid={testIds.ratingTableEditorAddColumnCondition}
                    onClick={handleAddColumnCondition}
                    color="primary"
                  >
                    <Add />
                  </IconButton>
                </TableCell>
              )}
              {!is2D && (
                <TableCell sx={{ minWidth: 400 }}>
                  <Typography variant="subtitle2" fontWeight="bold">
                    {t("rates", "Rates")}
                  </Typography>
                </TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {ratingTable.rowAxis.conditions.map((rowCondition, rowIndex) => (
              <TableRow key={rowIndex}>
                <TableCell>
                  <Stack spacing={1} direction="row">
                    <ConditionValueSelector
                      target={rowAxisConfig.target}
                      operator={rowAxisConfig.operator}
                      value={rowCondition.value}
                      customer={customer}
                      placeholder={`Row ${rowIndex + 1} value`}
                      onChange={(value) => {
                        onChange({
                          ...ratingTable,
                          rowAxis: {
                            conditions: ratingTable.rowAxis.conditions.map(
                              (c, i) => (i === rowIndex ? { ...c, value } : c)
                            ),
                          },
                        });
                      }}
                    />
                    <Box display="flex" justifyContent="center">
                      <IconButton data-testid={`${
                          testIds.ratingTableEditorDelete
                        }-${String(rowCondition.value)}`}
                        size="small"
                        onClick={() => handleRemoveRowCondition(rowIndex)}
                        color="error"
                        disabled={ratingTable.rowAxis.conditions.length <= 1}
                      >
                        <Delete />
                      </IconButton>
                    </Box>
                  </Stack>
                </TableCell>
                {is2D ? (
                  ratingTable.cells[rowIndex]?.map((cell, colIndex) => (
                    <TableCell key={colIndex}>
                      <RateForm
                        rate={
                          cell.rates[0] || {
                            method: BillingMethod.FlatFee,
                            amount: 0,
                            label: "",
                            referenceNumber: "",
                            parameters: [],
                          }
                        }
                        ruleType={rateTableTypeToBillingRuleType(
                          ratingTable.rateType
                        )}
                        compact
                        showChargeLabel
                        showTaxable={showTaxable}
                        customer={customer}
                        onChange={(updatedRate) => {
                          const newCells = [...ratingTable.cells];
                          if (!newCells[rowIndex]) {
                            newCells[rowIndex] = [];
                          }
                          if (!newCells[rowIndex][colIndex]) {
                            newCells[rowIndex][colIndex] = { rates: [] };
                          }
                          newCells[rowIndex][colIndex] = {
                            rates: [updatedRate],
                          };
                          onChange({
                            ...ratingTable,
                            cells: newCells,
                          });
                        }}
                      />
                    </TableCell>
                  ))
                ) : (
                  <TableCell>
                    <RateForm
                      rate={
                        ratingTable.cells[rowIndex]?.[0]?.rates[0] || {
                          method: BillingMethod.FlatFee,
                          amount: 0,
                          label: "",
                          referenceNumber: "",
                          parameters: [],
                        }
                      }
                      ruleType={rateTableTypeToBillingRuleType(
                        ratingTable.rateType
                      )}
                      compact
                      showChargeLabel
                      showTaxable={showTaxable}
                      customer={customer}
                      onChange={(updatedRate) => {
                        const newCells = [...ratingTable.cells];
                        if (!newCells[rowIndex]) {
                          newCells[rowIndex] = [{ rates: [] }];
                        }
                        newCells[rowIndex][0] = {
                          rates: [updatedRate],
                        };
                        onChange({
                          ...ratingTable,
                          cells: newCells,
                        });
                      }}
                    />
                  </TableCell>
                )}
              </TableRow>
            ))}
            <TableRow>
              <TableCell>
                <Button data-testid={testIds.ratingTableEditorAddRowCondition}
                  startIcon={<Add />}
                  onClick={handleAddRowCondition}
                  variant="outlined"
                  size="small"
                  fullWidth
                >
                  {t("addRowCondition", "Add Row Condition")}
                </Button>
              </TableCell>
              {is2D &&
                ratingTable.columnAxis?.conditions.map((_, index) => (
                  <TableCell key={index} />
                ))}
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      <Divider sx={{ my: 3 }} />

      <Box display="flex" justifyContent="flex-end" alignItems="center" gap={2}>
        <Button data-testid={testIds.ratingTableEditorCancel} variant="outlined" onClick={onCancel} disabled={saving}>
          {t("cancel", "Cancel")}
        </Button>
        <Button data-testid={testIds.ratingTableEditorSaveChanges}
          variant="contained"
          color="primary"
          onClick={onSave}
          disabled={saving || !isValidTable()}
        >
          {t("saveChanges", "Save Changes")}
        </Button>
      </Box>
    </Box>
  );
};

export default RatingTableEditor;

function rateTableTypeToBillingRuleType(rateTableType: RateTableType) {
  switch (rateTableType) {
    case RateTableType.Primary:
      return BillingRuleType.Primary;
    case RateTableType.Accessorial:
      return BillingRuleType.Accessorial;
    case RateTableType.Tax:
      return BillingRuleType.Tax;
    default:
      return BillingRuleType.Primary;
  }
}
